import React from "react";
import { Link } from "react-router-dom";
import {
  Wrench, Layers, Factory, ArrowRight, CheckCircle2, XCircle, Info,
  ShieldCheck, Truck, Boxes, Ruler, FlaskConical, Beaker, Zap,
} from "lucide-react";
import PipeClampFinder from "../components/PipeClampFinder";
import useSeoHead from "../hooks/useSeoHead";
import { SIZE_TABLE } from "../lib/conversions";

const PROD_URL = "https://stainlesspipeclamps.store";
const PAGE_PATH = "/stainless-steel-pipe-clamps";
const CANONICAL = `${PROD_URL}${PAGE_PATH}`;

const FAQ = [
  {
    q: "Which pipe clamp fits a DN50 pipe?",
    a: "DN50 (also written as 2\" nominal bore) corresponds to a pipe outside diameter of 60.3 mm. A DIN 3567 clamp sized 60.3 mm in V2A (304) or V4A (316) stainless steel fits a DN50 schedule 10 / 40 carbon or stainless pipe directly. Use our Pipe Clamp Finder above to confirm the strip width (commonly 30×4 mm for DN50) and bolt size (M10) for your span.",
  },
  {
    q: "What is the difference between V2A and V4A pipe clamps?",
    a: "V2A (DIN designation for AISI 304 / 1.4301) is the standard austenitic stainless steel: 18% chromium, 8% nickel, good corrosion resistance in dry indoor, food, potable water and general HVAC environments. V4A (AISI 316 / 1.4401) adds 2-3% molybdenum which dramatically improves resistance to chloride pitting — required for marine atmospheres, offshore platforms, chemical process, pharmaceutical and any application exposed to salt spray or industrial chlorides. For continuous chloride immersion, even 316 reaches its limits — duplex 1.4462 is the next step up.",
  },
  {
    q: "How do I convert DN to inch pipe size?",
    a: "DN (Diamètre Nominal) is a metric pipe size designator that approximates — but does not exactly equal — the inch equivalent. Standard conversions per Nibro: DN15 = 3/8\" (18.0 mm OD), DN20 = 1/2\" (22.0 mm), DN25 ≈ no nominal inch (28.0 mm), DN32 = 1\" (33.7 mm), DN40 = 1 1/2\" (48.3 mm), DN50 = 2\" (60.3 mm), DN65 = 2 1/2\" (76.1 mm), DN80 = 3\" (88.9 mm), DN100 = 4\" (114.3 mm). Always size a pipe clamp on the actual outside diameter — not the nominal bore.",
  },
  {
    q: "Which stainless steel pipe clamp should I use outdoors?",
    a: "For sheltered outdoor installations (HVAC chillers, rainwater downpipes, roof plant): V2A / 304 is adequate. For coastal locations within 10 km of seawater, industrial atmospheres with SO₂ or NOx, or any installation exposed to deicing salts: specify V4A / 316. Polished finish further reduces crevice corrosion sites. For aggressive chemical or saline immersion contact us for duplex 1.4462 alternatives.",
  },
  {
    q: "What is DIN 3567?",
    a: "DIN 3567 is the German industrial standard that specifies dimensions and tolerances for two-piece bolted pipe clamps in metric strip sizes. It defines three forms: Form A (light-duty, single bolt, smaller diameters), Form B (medium-duty, two-bolt design, standard piping support), and Form C (heavy-duty, reinforced strip and threaded boss for additional load take-out). All Nibro DIN 3567 clamps are manufactured to the dimensional envelope of the standard in either V2A or V4A stainless.",
  },
];

const OD_SIZES = [
  { dn: 15, od: 18.0, inch: '3/8"' },
  { dn: 20, od: 22.0, inch: '1/2"' },
  { dn: 25, od: 28.0, inch: "—" },
  { dn: 32, od: 33.7, inch: '1"' },
  { dn: 40, od: 48.3, inch: '1 1/2"' },
  { dn: 50, od: 60.3, inch: '2"' },
  { dn: 65, od: 76.1, inch: '2 1/2"' },
  { dn: 80, od: 88.9, inch: '3"' },
  { dn: 100, od: 114.3, inch: '4"' },
  { dn: 125, od: 139.7, inch: '5"' },
  { dn: 150, od: 168.3, inch: '6"' },
  { dn: 200, od: 219.1, inch: '8"' },
];

export default function StainlessSteelPipeClampsPage() {
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    "headline": "Stainless Steel Pipe Clamps – Find the Correct DIN 3567 Clamp",
    "description": "Find the correct stainless steel pipe clamp by DN, inch or diameter. DIN 3567 pipe clamps in V2A and V4A delivered directly from the manufacturer.",
    "datePublished": "2026-05-29",
    "dateModified": "2026-05-29",
    "url": CANONICAL,
    "mainEntityOfPage": CANONICAL,
    "author": {
      "@type": "Organization",
      "name": "ARAMFIX — NIBRO",
      "url": PROD_URL,
    },
    "publisher": {
      "@type": "Organization",
      "name": "ARAMFIX — NIBRO",
      "url": PROD_URL,
    },
    "keywords": "stainless steel pipe clamps, DIN 3567, V2A pipe clamp, V4A pipe clamp, 304 stainless steel pipe clamp, 316 stainless steel pipe clamp, DN pipe clamp, pipe clamp manufacturer Europe",
    "about": [
      { "@type": "Thing", "name": "DIN 3567 stainless steel pipe clamp" },
      { "@type": "Thing", "name": "Industrial pipe support" },
    ],
  };
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": PROD_URL },
      { "@type": "ListItem", "position": 2, "name": "Stainless Steel Pipe Clamps", "item": CANONICAL },
    ],
  };

  useSeoHead({
    title: "Stainless Steel Pipe Clamps | DIN 3567 Factory Direct — ARAMFIX",
    description: "Find the correct stainless steel pipe clamp by DN, inch or diameter. DIN 3567 pipe clamps in V2A and V4A delivered directly from the manufacturer.",
    canonical: CANONICAL,
    meta: [
      { property: "og:type", content: "article" },
      { property: "og:title", content: "Stainless Steel Pipe Clamps — DIN 3567 Factory Direct" },
      { property: "og:description", content: "Find the correct stainless steel pipe clamp by DN, inch or diameter. V2A and V4A direct from the European manufacturer." },
      { property: "og:url", content: CANONICAL },
      { property: "og:image", content: `${PROD_URL}/aramfix-hero.jpg` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "keywords", content: "stainless steel pipe clamps, pipe clamp DIN 3567, DIN 3567 stainless steel, Rohrschelle Edelstahl, V2A pipe clamp, V4A pipe clamp, 316 stainless steel pipe clamp, pipe clamp manufacturer Europe, DN to inch pipe clamp, stainless pipe support" },
    ],
    jsonLd: [articleLd, faqLd, breadcrumbLd],
  });

  return (
    <main className="bg-white" data-testid="ssc-landing-page">
      {/* ────────── Hero ────────── */}
      <section className="border-b border-zinc-200 bg-gradient-to-br from-nibro-sage-pale via-white to-zinc-50">
        <div className="mx-auto max-w-5xl px-6 py-16 lg:py-24">
          <div className="font-mono text-[11px] font-bold uppercase tracking-widest text-aramfix-red">
            Factory direct · DIN 3567 · V2A / V4A
          </div>
          <h1 className="mt-4 font-display text-4xl font-bold leading-tight text-zinc-900 sm:text-5xl lg:text-6xl">
            Stainless Steel Pipe Clamps – Find the Correct DIN 3567 Clamp
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-zinc-700">
            Two-piece bolted pipe supports manufactured to DIN 3567 in
            AISI 304 (V2A) and AISI 316 (V4A) stainless steel. Sized by
            DN, inch or outside diameter, dispatched within 24 hours
            across Europe from the Nibro plant in the Netherlands.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#finder" className="inline-flex items-center gap-2 rounded-md bg-aramfix-red px-6 py-3 text-sm font-bold uppercase tracking-wider text-white transition hover:bg-aramfix-red/90" data-testid="ssc-hero-finder-cta">
              <Wrench size={16} /> Open the Pipe Clamp Finder
            </a>
            <Link to="/products" className="inline-flex items-center gap-2 rounded-md border-2 border-zinc-300 bg-white px-6 py-3 text-sm font-bold uppercase tracking-wider text-zinc-800 transition hover:border-nibro-sage hover:text-nibro-sage-dark" data-testid="ssc-hero-catalog-cta">
              <Boxes size={16} /> Browse the catalog
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-3 text-sm text-zinc-600 sm:grid-cols-4">
            {[
              ["24 h", "Dispatch"],
              ["V2A · V4A", "In stock"],
              ["DIN 3567", "Form A · B · C"],
              ["DN10 – DN300", "Range"],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="font-display text-xl font-bold text-zinc-900">{k}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-6 py-12 prose prose-zinc max-w-none lg:py-16">

        {/* ────────── What is a pipe clamp ────────── */}
        <section>
          <h2 className="font-display text-3xl font-bold text-zinc-900">What Is a Stainless Steel Pipe Clamp?</h2>
          <p>
            A <strong>stainless steel pipe clamp</strong> — also called a
            pipe support or pipe bracket — is a two-piece bolted band that
            grips a fluid-carrying pipe and transfers its weight, thermal
            expansion and dynamic loads into a structural anchor (wall,
            ceiling, beam, channel, threaded rod). On industrial piping
            systems it replaces softer rubber-lined wall hooks because it
            offers controlled clamping force, repeatable lateral spacing
            and a corrosion envelope that matches the pipe it supports.
          </p>
          <p>
            The DIN 3567 norm specifies dimensions for the most common
            metric bolted variant. Each clamp consists of two semi-circular
            halves of stainless strip, joined by hex-head bolts (typically
            M8, M10 or M12 depending on diameter and load class), and a
            threaded boss or weld-plate at the back for fastening to the
            structure. Larger Form C variants reinforce the strip
            cross-section so the clamp can take both axial and lateral
            loads, suitable for thermally cycled steam and refrigerant
            piping.
          </p>
          <p>
            Typical applications across the industries we supply:
          </p>
          <ul>
            <li><strong>Piping systems</strong> — utility water, compressed air, glycol cooling loops, gas distribution.</li>
            <li><strong>Food &amp; beverage</strong> — brewery utility piping, dairy CIP/SIP lines, edible-oil refining.</li>
            <li><strong>Pharmaceutical plants</strong> — purified water (PW) and WFI loops, clean steam, process gas headers.</li>
            <li><strong>Water treatment</strong> — RO skid manifolds, dosing lines, sludge return piping.</li>
            <li><strong>Process industry</strong> — heat exchangers, condensate return, intermediate chemical transfer.</li>
            <li><strong>HVAC &amp; mechanical contracting</strong> — chilled water, condenser water, refrigerant suction lines.</li>
          </ul>
        </section>

        {/* ────────── How to select ────────── */}
        <section className="mt-16">
          <h2 className="font-display text-3xl font-bold text-zinc-900">How To Select The Correct Pipe Clamp</h2>
          <p>
            Selecting a pipe clamp follows three deterministic steps. Skip
            none of them — undersized strip width is the most common cause
            of installation failures we see in the field.
          </p>

          <h3 className="mt-8 font-display text-xl font-bold">1 · Determine the pipe outside diameter (OD)</h3>
          <p>
            Always size the clamp on the pipe&apos;s <em>actual</em> outside
            diameter, never on the nominal bore (DN) or the nominal inch
            size. A schedule-10 stainless pipe and a schedule-40 carbon
            pipe of the same DN have similar — but not identical — outside
            diameters. The Pipe Clamp Finder below converts DN ↔ inch ↔ OD
            against the Nibro mapping so the variant you order matches the
            pipe in the trench.
          </p>

          <h3 className="mt-8 font-display text-xl font-bold">2 · Choose the stainless grade</h3>
          <p>
            <strong>V2A / 304</strong> for clean indoor, food and potable water
            applications. <strong>V4A / 316</strong> for any exposure to chlorides:
            coastal, industrial, swimming-pool plant rooms, pharmaceutical
            CIP cycles using sodium hypochlorite. When in doubt, pay the
            18-25% premium for 316 — corrosion damage and the cost of
            shutdown to re-clamp a line dwarfs the upgrade cost.
          </p>

          <h3 className="mt-8 font-display text-xl font-bold">3 · Select form (A / B / C) and strip size</h3>
          <p>
            Form A is suitable for diameters up to DN50 in light service.
            Form B is the workhorse for DN15-DN150 utility piping. Form C
            is mandatory above DN150 and any line carrying thermal loads
            above 60 °C. Strip dimensions (20×3, 25×3, 30×4, 40×5, 50×8 mm)
            scale with diameter and load class — every Nibro detail page
            lists the supplied strip per SKU.
          </p>
        </section>

        {/* ────────── DN to Inch conversion ────────── */}
        <section className="mt-16" id="dn-inch">
          <h2 className="font-display text-3xl font-bold text-zinc-900">DN To Inch Conversion Table</h2>
          <p>
            The DN ↔ inch ↔ outside-diameter relationship is non-linear
            for small bores. The table below reflects the canonical Nibro
            mapping as used by every clamp variant we ship.
          </p>
          <div className="not-prose mt-6 overflow-hidden rounded-md border border-zinc-200">
            <table className="w-full text-sm" data-testid="ssc-dn-inch-table">
              <thead className="bg-zinc-100 font-mono text-[10px] uppercase tracking-widest text-zinc-700">
                <tr>
                  <th className="px-3 py-2 text-left">DN</th>
                  <th className="px-3 py-2 text-left">Nominal Inch</th>
                  <th className="px-3 py-2 text-right">Outside Diameter (mm)</th>
                </tr>
              </thead>
              <tbody className="font-mono text-xs">
                {SIZE_TABLE.map((r) => (
                  <tr key={r.dn} className="border-t border-zinc-100">
                    <td className="px-3 py-1.5 font-bold text-zinc-900">DN{r.dn}</td>
                    <td className="px-3 py-1.5">{r.inch || "—"}</td>
                    <td className="px-3 py-1.5 text-right">{r.d1_mm} mm</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm">
            For an interactive lookup, scroll down to the{" "}
            <a href="#finder" className="font-bold text-nibro-sage-dark">Pipe Clamp Finder</a>
            {" "}— it auto-converts between DN, inch and millimeter input and proposes
            the matching SKU across all DIN 3567 families.
          </p>
        </section>

        {/* ────────── Outside Diameter table ────────── */}
        <section className="mt-16">
          <h2 className="font-display text-3xl font-bold text-zinc-900">Pipe Outside Diameter Table</h2>
          <p>
            Pipe outside diameters are standardised by EN 10220 (metric)
            and ASME B36.10/19 (imperial). The values below are the most
            common targets for stainless steel pipe clamps in industrial
            service:
          </p>
          <div className="not-prose mt-6 grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {OD_SIZES.map((s) => (
              <div key={s.dn} className="rounded-md border border-zinc-200 bg-white px-3 py-2 text-center">
                <div className="font-display text-base font-bold text-zinc-900">{s.od} mm</div>
                <div className="font-mono text-[10px] text-zinc-500">DN{s.dn} · {s.inch}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ────────── DIN 3567 explained ────────── */}
        <section className="mt-16">
          <h2 className="font-display text-3xl font-bold text-zinc-900">DIN 3567 Explained</h2>
          <p>
            DIN 3567 defines bolted, two-piece pipe clamps in three
            geometric forms. All three are interchangeable for mounting
            interface (back-stud M10) but differ in strip cross-section
            and rated load:
          </p>
          <div className="not-prose mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
            {[
              { form: "A", title: "Light-duty", lead: "Single-bolt closure, suitable for ≤ DN50 utility piping, refrigerant suction lines, instrumentation tubing." },
              { form: "B", title: "Standard", lead: "Two-bolt closure with reinforced strip — the default for DN15-DN150 utility, glycol and chilled-water service. Workhorse of the range." },
              { form: "C", title: "Heavy-duty", lead: "Reinforced strip cross-section + threaded boss; mandatory for ≥ DN150 or thermally cycled service above 60 °C." },
            ].map((f) => (
              <div key={f.form} className="rounded-md border-2 border-nibro-sage/40 bg-white p-5">
                <div className="font-display text-2xl font-bold text-aramfix-red">Form {f.form}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{f.title}</div>
                <p className="mt-3 text-sm leading-relaxed text-zinc-700">{f.lead}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ────────── 304 vs 316 ────────── */}
        <section className="mt-16" id="material">
          <h2 className="font-display text-3xl font-bold text-zinc-900">304 vs 316 Stainless Steel Pipe Clamps</h2>
          <p>
            The choice between 304 (V2A) and 316 (V4A) is driven entirely
            by the chloride environment your clamps will see. Both grades
            are food-contact compliant; both survive normal indoor service
            indefinitely. The breakpoint is exposure to chloride ions
            beyond ~50 ppm dissolved or any atmospheric chloride
            deposition.
          </p>
          <div className="not-prose mt-6 overflow-hidden rounded-md border border-zinc-200">
            <table className="w-full text-sm">
              <thead className="bg-zinc-100 font-mono text-[10px] uppercase tracking-widest text-zinc-700">
                <tr>
                  <th className="px-3 py-2 text-left">Environment</th>
                  <th className="px-3 py-2 text-center">304 (V2A)</th>
                  <th className="px-3 py-2 text-center">316 (V4A)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {[
                  ["Indoor dry", "✓ Optimal", "✓ Optimal"],
                  ["Food &amp; beverage", "✓ Optimal", "✓ Optimal (preferred for caustic CIP)"],
                  ["Outdoor sheltered", "✓ OK", "✓ Optimal"],
                  ["Coastal / marine", "✗ Avoid", "✓ Required"],
                  ["Pharmaceutical CIP", "✗ Avoid", "✓ Required"],
                  ["Chemical process", "✗ Case-by-case", "✓ Preferred"],
                  ["Swimming-pool plant", "✗ Not supplied", "✗ Not supplied (duplex only)"],
                ].map((row) => (
                  <tr key={row[0]} className="border-t border-zinc-100">
                    <td className="px-3 py-2 font-semibold text-zinc-800" dangerouslySetInnerHTML={{ __html: row[0] }} />
                    <td className="px-3 py-2 text-center font-mono text-xs">{row[1]}</td>
                    <td className="px-3 py-2 text-center font-mono text-xs">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm text-zinc-600">
            Note: ARAMFIX does <strong>not</strong> supply 304 or 316 clamps for
            swimming-pool plant rooms — chloramine atmospheres above the
            pool deck cause stress-corrosion cracking in austenitic
            stainless. For pool work contact{" "}
            <a href="mailto:info@aramfix.com" className="font-bold text-nibro-sage-dark">info@aramfix.com</a>
            {" "}for duplex 1.4462 alternatives.
          </p>
        </section>

        {/* ────────── Finder ────────── */}
        <section className="mt-16" id="finder">
          <h2 className="font-display text-3xl font-bold text-zinc-900">Pipe Clamp Finder Tool</h2>
          <p>
            Enter the pipe diameter in DN, inch <em>or</em> millimeter. The
            finder converts to the other two units, queries the live Nibro
            catalog and returns every DIN 3567 family that carries that
            exact size — with stock status, price and the option to add to
            quote in one click.
          </p>
          <div className="not-prose mt-6">
            <PipeClampFinder lang="en" />
          </div>
        </section>

        {/* ────────── Why direct ────────── */}
        <section className="mt-16">
          <h2 className="font-display text-3xl font-bold text-zinc-900">Why Buy Direct From The Manufacturer?</h2>
          <p>
            Pipe clamps are commodity hardware on most distributor
            catalogues — bundled inside multi-vendor SKU databases, marked
            up two or three times between the stamping press and the
            installer&apos;s skid. Buying direct from Nibro cuts that chain.
          </p>
          <div className="not-prose mt-6 overflow-hidden rounded-md border border-zinc-200">
            <table className="w-full text-sm">
              <thead className="bg-zinc-100 font-mono text-[10px] uppercase tracking-widest text-zinc-700">
                <tr>
                  <th className="px-3 py-2 text-left">Attribute</th>
                  <th className="px-3 py-2 text-center">Factory Direct</th>
                  <th className="px-3 py-2 text-center">Distributor</th>
                  <th className="px-3 py-2 text-center">Wholesaler</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {[
                  ["Lead time", "24 h", "3-7 days", "1-2 weeks"],
                  ["Price (DN50)", "Factory floor", "+25-40%", "+40-70%"],
                  ["Range &amp; sizes", "Full + custom", "Top 80%", "Top 30%"],
                  ["Technical advice", "Engineering desk", "Sales desk", "Catalogue lookup"],
                  ["MOQ", "1 piece", "Box-pack", "Pallet"],
                ].map((row) => (
                  <tr key={row[0]} className="border-t border-zinc-100">
                    <td className="px-3 py-2 font-semibold text-zinc-800" dangerouslySetInnerHTML={{ __html: row[0] }} />
                    <td className="px-3 py-2 text-center font-mono text-xs text-emerald-700">{row[1]}</td>
                    <td className="px-3 py-2 text-center font-mono text-xs text-zinc-600">{row[2]}</td>
                    <td className="px-3 py-2 text-center font-mono text-xs text-zinc-500">{row[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ────────── Industries ────────── */}
        <section className="mt-16">
          <h2 className="font-display text-3xl font-bold text-zinc-900">Industries We Supply</h2>
          <div className="not-prose mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {[
              { i: FlaskConical, t: "Food &amp; beverage", k: "brewery, dairy, edible oil" },
              { i: Beaker, t: "Pharmaceutical", k: "PW, WFI, clean steam" },
              { i: Zap, t: "Water treatment", k: "RO skids, dosing, biogas" },
              { i: Factory, t: "Chemical process", k: "intermediate transfer, heat exchanger" },
              { i: Wrench, t: "Mechanical contracting", k: "chilled water, HVAC plant" },
              { i: Layers, t: "OEM &amp; skid builders", k: "private label, custom kits" },
            ].map((c) => (
              <div key={c.t} className="rounded-md border border-zinc-200 bg-white p-4">
                <c.i className="text-aramfix-red" size={20} />
                <div className="mt-2 font-display text-sm font-bold text-zinc-900" dangerouslySetInnerHTML={{ __html: c.t }} />
                <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-zinc-500">{c.k}</div>
              </div>
            ))}
          </div>
          <p className="mt-6">
            See more on our{" "}
            <Link to="/industries" className="font-bold text-nibro-sage-dark">industries reference page</Link>
            {" "}— each sector page links to the SKUs we ship most often.
          </p>
        </section>

        {/* ────────── Available sizes ────────── */}
        <section className="mt-16">
          <h2 className="font-display text-3xl font-bold text-zinc-900">Available Sizes</h2>
          <p>
            Every standard DN/inch from DN10 to DN300 is stocked or
            available within 5 working days. Strip widths run from 20×3 mm
            up to 50×8 mm for heavy-duty Form C variants.
          </p>
          <div className="not-prose mt-6 flex flex-wrap gap-2">
            {SIZE_TABLE.map((s) => (
              <Link
                key={s.dn}
                to={`/products?dn=${s.dn}`}
                className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-1.5 font-mono text-xs text-zinc-700 transition hover:border-nibro-sage hover:bg-nibro-sage-pale"
                data-testid={`ssc-size-link-dn${s.dn}`}
              >
                DN{s.dn} · {s.d1_mm} mm
              </Link>
            ))}
          </div>
        </section>

        {/* ────────── FAQ ────────── */}
        <section className="mt-16">
          <h2 className="font-display text-3xl font-bold text-zinc-900">Frequently Asked Questions</h2>
          <div className="not-prose mt-6 space-y-4">
            {FAQ.map((f, i) => (
              <details key={i} className="group rounded-md border border-zinc-200 bg-white p-4">
                <summary className="cursor-pointer font-display font-bold text-zinc-900 group-open:text-aramfix-red">
                  {f.q}
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-zinc-700">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ────────── Internal links ────────── */}
        <section className="mt-16">
          <h2 className="font-display text-3xl font-bold text-zinc-900">Browse Our Catalog</h2>
          <div className="not-prose mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {[
              ["DIN 3567 category", "/products?search=DIN+3567"],
              ["V2A · 304 products", "/products?material=304"],
              ["V4A · 316 products", "/products?material=316"],
              ["Custom (maatwerk)", "/configurator"],
              ["About ARAMFIX", "/about"],
              ["Contact", "/contact"],
            ].map(([t, h]) => (
              <Link key={h} to={h} className="flex items-center justify-between rounded-md border border-zinc-200 bg-white px-4 py-3 font-display text-sm font-bold text-zinc-900 transition hover:border-nibro-sage hover:text-nibro-sage-dark">
                {t} <ArrowRight size={14} />
              </Link>
            ))}
          </div>
        </section>

        {/* ────────── Final CTA ────────── */}
        <section className="mt-16 rounded-lg border-2 border-aramfix-red/20 bg-gradient-to-br from-aramfix-red/5 to-nibro-sage-pale p-8 not-prose">
          <h2 className="font-display text-3xl font-bold text-zinc-900">Order Stainless Steel Pipe Clamps Online</h2>
          <p className="mt-3 max-w-2xl text-zinc-700">
            Find the SKU in our finder, add to quote, and pay on invoice
            or via Stripe. Bulk orders, custom geometries and distributor
            pricing are handled via direct contact.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href="#finder" className="inline-flex items-center gap-2 rounded-md bg-aramfix-red px-6 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-aramfix-red/90" data-testid="ssc-cta-finder">
              <Wrench size={16} /> Use the Finder
            </a>
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-md border-2 border-zinc-300 bg-white px-6 py-3 text-sm font-bold uppercase tracking-wider text-zinc-800 hover:border-nibro-sage" data-testid="ssc-cta-quote">
              <ShieldCheck size={16} /> Request a quote
            </Link>
            <Link to="/distributor/login" className="inline-flex items-center gap-2 rounded-md border-2 border-zinc-300 bg-white px-6 py-3 text-sm font-bold uppercase tracking-wider text-zinc-800 hover:border-nibro-sage" data-testid="ssc-cta-distributor">
              <Truck size={16} /> Distributor portal
            </Link>
          </div>
        </section>

      </article>
    </main>
  );
}
